import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { useMe } from '@/hooks/useMe'
import { useSystemToolsRedis } from '@/hooks/useSystemToolsRedis'
import {
  Database,
  Search,
  Trash2,
  Eye,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  Loader2,
} from 'lucide-react'
import { toast } from 'sonner'

// 快速搜尋的預設 patterns
const quickPatterns = [
  { label: '角色權限', pattern: 'role:permissions:*' },
  { label: 'Session', pattern: 'session:*' },
  { label: '裝置狀態', pattern: 'device:*' },
]

export default function RedisToolPage() {
  const navigate = useNavigate()
  const { user, isLoading: userLoading } = useMe()
  const { searchKeys, getKeyValue, deleteKeys } = useSystemToolsRedis()

  const [pattern, setPattern] = useState('role:permissions:*')
  const [keys, setKeys] = useState<{ key: string; ttl: number }[]>([])
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [keyValues, setKeyValues] = useState<Record<string, { value: string; type: string }>>({})
  const [isSearching, setIsSearching] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [keysToDelete, setKeysToDelete] = useState<string[]>([])
  const [searchResult, setSearchResult] = useState<{
    total: number
    truncated: boolean
  } | null>(null)

  // 權限檢查
  if (!userLoading && user?.role_type !== 'sys_admin') {
    navigate('/')
    return null
  }

  const handleSearch = async () => {
    if (!pattern.trim()) {
      toast.error('請輸入搜尋 pattern')
      return
    }

    setIsSearching(true)
    setKeys([])
    setSelectedKeys(new Set())
    setExpandedKey(null)
    setKeyValues({})
    setSearchResult(null)

    try {
      const result = await searchKeys(pattern)
      setKeys(result.keys)
      setSearchResult({
        total: result.total,
        truncated: result.truncated,
      })
    } catch {
      toast.error('搜尋失敗')
    } finally {
      setIsSearching(false)
    }
  }

  const handleViewKey = async (key: string) => {
    if (expandedKey === key) {
      setExpandedKey(null)
      return
    }

    if (!keyValues[key]) {
      try {
        const result = await getKeyValue(key)
        setKeyValues((prev) => ({
          ...prev,
          [key]: { value: result.value, type: result.type },
        }))
      } catch {
        toast.error('取得值失敗')
        return
      }
    }

    setExpandedKey(key)
  }

  const handleDeleteKey = (key: string) => {
    setKeysToDelete([key])
    setDeleteDialogOpen(true)
  }

  const handleDeleteSelected = () => {
    if (selectedKeys.size === 0) {
      toast.error('請先選擇要刪除的 keys')
      return
    }
    setKeysToDelete(Array.from(selectedKeys))
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    setIsDeleting(true)
    try {
      const result = await deleteKeys(keysToDelete)
      toast.success(`已刪除 ${result.deleted} 個 keys`)

      // 從列表中移除已刪除的 keys
      setKeys((prev) => prev.filter((k) => !keysToDelete.includes(k.key)))
      setSelectedKeys((prev) => {
        const newSet = new Set(prev)
        keysToDelete.forEach((k) => newSet.delete(k))
        return newSet
      })

      // 清除展開的 key
      if (expandedKey && keysToDelete.includes(expandedKey)) {
        setExpandedKey(null)
      }
    } catch {
      toast.error('刪除失敗')
    } finally {
      setIsDeleting(false)
      setDeleteDialogOpen(false)
      setKeysToDelete([])
    }
  }

  const toggleSelectAll = () => {
    if (selectedKeys.size === keys.length) {
      setSelectedKeys(new Set())
    } else {
      setSelectedKeys(new Set(keys.map((k) => k.key)))
    }
  }

  const toggleSelectKey = (key: string) => {
    setSelectedKeys((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(key)) {
        newSet.delete(key)
      } else {
        newSet.add(key)
      }
      return newSet
    })
  }

  const formatTTL = (ttl: number): string => {
    if (ttl === -1) return '永不過期'
    if (ttl === -2) return '已過期'
    if (ttl < 60) return `${ttl} 秒`
    if (ttl < 3600) return `${Math.floor(ttl / 60)} 分鐘`
    if (ttl < 86400) return `${Math.floor(ttl / 3600)} 小時`
    return `${Math.floor(ttl / 86400)} 天`
  }

  const formatValue = (value: string): string => {
    try {
      const parsed = JSON.parse(value)
      return JSON.stringify(parsed, null, 2)
    } catch {
      return value
    }
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/system-tools')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Database className="h-8 w-8" />
            Redis 快取管理
          </h1>
          <p className="text-muted-foreground mt-1">查看與清除 Redis 快取資料</p>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">搜尋 Keys</CardTitle>
          <CardDescription>輸入 pattern 搜尋 Redis keys（支援 * 萬用字元）</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="例如: role:permissions:*"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch} disabled={isSearching}>
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              <span className="ml-2">搜尋</span>
            </Button>
          </div>

          <div className="flex gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground">快速搜尋:</span>
            {quickPatterns.map((qp) => (
              <Badge
                key={qp.pattern}
                variant="outline"
                className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                onClick={() => {
                  setPattern(qp.pattern)
                }}
              >
                {qp.label}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {searchResult && (
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            找到 {searchResult.total} 個 keys
            {searchResult.truncated && '（僅顯示前 100 個）'}
          </div>
          {keys.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeleteSelected}
              disabled={selectedKeys.size === 0}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              刪除選中項目 ({selectedKeys.size})
            </Button>
          )}
        </div>
      )}

      {keys.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="border-b px-4 py-3 flex items-center gap-4">
              <Checkbox
                checked={selectedKeys.size === keys.length && keys.length > 0}
                onCheckedChange={toggleSelectAll}
              />
              <span className="text-sm font-medium">全選</span>
            </div>

            <div className="divide-y">
              {keys.map((keyInfo) => (
                <div key={keyInfo.key}>
                  <div className="px-4 py-3 flex items-center gap-4 hover:bg-muted/50">
                    <Checkbox
                      checked={selectedKeys.has(keyInfo.key)}
                      onCheckedChange={() => toggleSelectKey(keyInfo.key)}
                    />

                    <button
                      className="flex-1 text-left flex items-center gap-2"
                      onClick={() => handleViewKey(keyInfo.key)}
                    >
                      {expandedKey === keyInfo.key ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="font-mono text-sm">{keyInfo.key}</span>
                    </button>

                    <Badge variant="secondary" className="text-xs">
                      TTL: {formatTTL(keyInfo.ttl)}
                    </Badge>

                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleViewKey(keyInfo.key)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteKey(keyInfo.key)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {expandedKey === keyInfo.key && keyValues[keyInfo.key] && (
                    <div className="px-4 py-3 bg-muted/30 border-t">
                      <div className="text-xs text-muted-foreground mb-2">
                        Type: {keyValues[keyInfo.key].type}
                      </div>
                      <pre className="text-xs font-mono bg-background p-3 rounded border overflow-auto max-h-64">
                        {formatValue(keyValues[keyInfo.key].value)}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {searchResult && keys.length === 0 && (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">沒有找到符合的 keys</p>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確認刪除</AlertDialogTitle>
            <AlertDialogDescription>
              確定要刪除以下 {keysToDelete.length} 個 keys 嗎？此操作無法復原。
              <div className="mt-4 max-h-40 overflow-auto">
                {keysToDelete.map((key) => (
                  <div key={key} className="font-mono text-xs py-1">
                    {key}
                  </div>
                ))}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              確認刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
